import type { AutosaveStatus } from '../hooks/useAutosave.ts';

export interface SaveIndicatorProps {
  status: AutosaveStatus;
  /** The document changed elsewhere — a different problem from a failed write. */
  conflicted?: boolean;
}

/**
 * Autosave status.
 *
 * The wording is deliberately plain: a user seeing "saving" or "not saved"
 * needs to know whether their work is safe, not which exception was raised.
 * The underlying error goes to the console for whoever is debugging.
 */
export function SaveIndicator({ status, conflicted = false }: SaveIndicatorProps) {
  if (conflicted) {
    return (
      <span className="shrink-0 text-xs text-amber-700" role="status">
        Edited in another tab — reload to continue
      </span>
    );
  }

  const label: Record<AutosaveStatus, string> = {
    idle: 'All changes saved',
    pending: 'Editing…',
    saving: 'Saving…',
    saved: 'All changes saved',
    error: 'Not saved — retrying',
  };

  return (
    <span
      className={`shrink-0 text-xs ${status === 'error' ? 'text-red-700' : 'text-neutral-500'}`}
      role="status"
    >
      {label[status]}
    </span>
  );
}
